import React, { useState, useRef, useEffect } from 'react';
import { INDIAN_STATES } from '../../../utils/formHelpers';
import './StateSelect.css';

const StateSelect = ({ value, onChange, disabled = false, readOnly = false }) => {
  const [open, setOpen]         = useState(false);
  const [search, setSearch]     = useState('');
  const [addMode, setAddMode]   = useState(false);
  const [custom, setCustom]     = useState('');
  const wrapRef   = useRef(null);
  const searchRef = useRef(null);
  const customRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setSearch(''); setAddMode(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (open && !addMode && searchRef.current) searchRef.current.focus();
    if (addMode && customRef.current) customRef.current.focus();
  }, [open, addMode]);

  const filtered = INDIAN_STATES.filter(s => s.toLowerCase().includes(search.toLowerCase()));

  const select = (s) => { onChange(s); setOpen(false); setSearch(''); setAddMode(false); };

  const confirmAdd = () => {
    const v = custom.trim();
    if (v) { onChange(v); setOpen(false); setSearch(''); setAddMode(false); setCustom(''); }
  };

  if (readOnly) return <div className="ss-readonly">{value || '—'}</div>;

  return (
    <div className="ss-wrap" ref={wrapRef}>

      {/* Trigger button */}
      <button
        type="button"
        className={`ss-trigger${open ? ' ss-open' : ''}${disabled ? ' ss-disabled' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
      >
        <span className={`ss-val${!value ? ' ss-placeholder' : ''}`}>{value || 'Select State'}</span>
        <span className="ss-right">
          {value && !disabled && (
            <span className="ss-clear" onClick={e => { e.stopPropagation(); onChange(''); }}>✕</span>
          )}
          <span className={`ss-arrow${open ? ' ss-up' : ''}`}>▾</span>
        </span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="ss-panel">

          {addMode ? (
            /* ── Add custom state ── */
            <div className="ss-add-mode">
              <div className="ss-add-header">
                <button type="button" className="ss-back-btn" onClick={() => { setAddMode(false); setCustom(''); }}>← Back</button>
                <span className="ss-add-title">Enter State / UT Name</span>
              </div>
              <input
                ref={customRef}
                type="text"
                className="ss-add-input"
                placeholder="e.g. My State"
                value={custom}
                onChange={e => setCustom(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmAdd(); if (e.key === 'Escape') { setAddMode(false); setCustom(''); } }}
              />
              <button
                type="button"
                className="ss-add-confirm"
                onClick={confirmAdd}
                disabled={!custom.trim()}
              >
                ✓ Use This State
              </button>
            </div>
          ) : (
            /* ── Search + list ── */
            <>
              <div className="ss-search-wrap">
                <span className="ss-search-icon">⌕</span>
                <input
                  ref={searchRef}
                  type="text"
                  className="ss-search"
                  placeholder="Search state..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                    if (e.key === 'Enter' && filtered.length === 1) select(filtered[0]);
                  }}
                />
                {search && <button type="button" className="ss-search-clear" onClick={() => setSearch('')}>✕</button>}
              </div>

              <ul className="ss-list">
                {filtered.length > 0
                  ? filtered.map(s => (
                    <li key={s} className={`ss-item${value === s ? ' ss-selected' : ''}`} onClick={() => select(s)}>
                      {value === s && <span className="ss-check">✓</span>}
                      <span>{s}</span>
                    </li>
                  ))
                  : <li className="ss-empty">No match for "<b>{search}</b>"</li>
                }
              </ul>

              {/* Add option */}
              <div className="ss-add-row" onClick={() => setAddMode(true)}>
                <span className="ss-add-icon">＋</span>
                <span>{search ? `Add "${search}"` : 'Add other state'}</span>
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
};

export default StateSelect;
